import React, { useEffect, useState } from 'react';
import { Search, Send, AlertCircle } from 'lucide-react';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getProjectCode } from '../../utils/auth';
import { getAppList, AppListItem } from '../../utils/app';
import { getPinSendList, PinSendItem } from '../../utils/customer';
import { formatDateTimeFromApi } from '../../utils/dateTime';

const PinSendList: React.FC = () => {
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('pin-send-list');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [apps, setApps] = useState<AppListItem[]>([]);

  const [selectedAppCode, setSelectedAppCode] = useState('all');
  const [receiverInput, setReceiverInput] = useState('');
  const [searched, setSearched] = useState(false);

  const [pinSendItems, setPinSendItems] = useState<PinSendItem[]>([]);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAppSelectError = error === '앱을 선택해주세요.';

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  useEffect(() => {
    const fetchApps = async () => {
      const projectCode = projectDetail?.code || getProjectCode();
      if (!projectCode) return;

      try {
        setIsLoadingApps(true);
        const result = await getAppList(projectCode);
        setApps(result.apps || []);
      } catch (fetchError: any) {
        console.error('앱 목록 조회 오류:', fetchError);
        setError(fetchError?.message || '앱 목록을 불러오지 못했습니다.');
      } finally {
        setIsLoadingApps(false);
      }
    };

    if (projectDetail) {
      fetchApps();
    }
  }, [projectDetail]);

  const handleSearch = async () => {
    if (selectedAppCode === 'all') {
      setError('앱을 선택해주세요.');
      return;
    }

    const trimmedReceiver = receiverInput.trim();
    if (!trimmedReceiver) {
      setError('수신처를 입력해주세요.');
      return;
    }

    try {
      setIsLoadingList(true);
      setError(null);
      setSearched(true);

      const list = await getPinSendList({
        projectCode: projectDetail?.code || getProjectCode() || '',
        appCode: selectedAppCode,
        receiver: trimmedReceiver,
      });
      setPinSendItems(list);
    } catch (searchError: any) {
      console.error('핀 발송 목록 조회 오류:', searchError);
      setPinSendItems([]);
      setError(searchError?.message || CommonUtils.getNetworkErrorMessage(searchError));
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleReceiverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="핀 발송 조회"
          subtitle="앱/수신처 기준으로 핀 발송 내역을 조회합니다"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <select
                    value={selectedAppCode}
                    onChange={(e) => {
                      setSelectedAppCode(e.target.value);
                      if (error === '앱을 선택해주세요.' && e.target.value !== 'all') {
                        setError(null);
                      }
                    }}
                    disabled={isLoadingApps}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">앱 선택</option>
                    {apps.map((app) => (
                      <option key={app.code} value={app.code}>
                        {app.name} ({app.code})
                      </option>
                    ))}
                  </select>

                  <div className="flex flex-1 sm:max-w-md min-w-[200px] gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={receiverInput}
                        onChange={(e) => setReceiverInput(e.target.value)}
                        onKeyDown={handleReceiverKeyDown}
                        placeholder="수신처 입력"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      onClick={handleSearch}
                      disabled={isLoadingList || isLoadingApps}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center whitespace-nowrap"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      검색
                    </button>
                  </div>
                </div>
              </div>
              {isAppSelectError && (
                <p className="mt-3 text-sm text-red-600">앱을 선택해주세요.</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        핀
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        발송일시
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingList ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        </td>
                      </tr>
                    ) : !searched ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <Send className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">검색 버튼을 눌러 핀 발송 목록을 조회하세요.</p>
                          </div>
                        </td>
                      </tr>
                    ) : pinSendItems.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <Send className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">조회된 핀 발송 내역이 없습니다.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pinSendItems.map((item, index) => (
                        <tr key={`${item.pin}-${item.sentAt}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.pin}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDateTimeFromApi(item.sentAt) || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PinSendList;
